/* Phone-in-cab GPS broadcaster.
 *
 * Wraps navigator.geolocation.watchPosition and POSTs to /api/assets/{id}/ping
 * at a throttled cadence. Queues pings to IndexedDB when offline (re-uses the
 * existing ll-offline-queue infrastructure). Stops when stopped or the
 * component unmounts.
 *
 * Usage:
 *   const { online, last, count, error, start, stop } = usePhoneGPS({ assetId, intervalMs });
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { http, API } from "@/lib/api";
import { enqueue } from "@/lib/offlineQueue";

const DEFAULT_INTERVAL_MS = 30_000;

function metersBetween(a, b) {
  // Haversine distance in meters (small + cheap)
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

export function usePhoneGPS({ assetId, intervalMs = DEFAULT_INTERVAL_MS } = {}) {
  const [active, setActive] = useState(false);
  const [last, setLast] = useState(null);
  const [count, setCount] = useState(0);
  const [error, setError] = useState(null);
  const [permission, setPermission] = useState(null); // 'granted' | 'denied' | 'prompt'
  const watchIdRef = useRef(null);
  const lastPostedAtRef = useRef(0);
  const lastPostedPosRef = useRef(null);
  const queuedRef = useRef(false);

  const supported = typeof window !== "undefined" && "geolocation" in navigator;

  const post = useCallback(async (pos) => {
    const body = {
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      speed: pos.coords.speed != null ? pos.coords.speed * 2.23694 : undefined, // m/s → mph
      heading: pos.coords.heading != null && !isNaN(pos.coords.heading) ? pos.coords.heading : undefined,
      accuracy: pos.coords.accuracy,
      source: "phone",
    };
    try {
      if (!navigator.onLine) throw new Error("offline");
      await http.post(`/assets/${assetId}/ping`, body);
      setCount((c) => c + 1);
      queuedRef.current = false;
    } catch (e) {
      // Queue for replay when back online
      try {
        await enqueue({
          kind: "json",
          method: "POST",
          url: `${API}/assets/${assetId}/ping`,
          body,
        });
        queuedRef.current = true;
        setCount((c) => c + 1);
      } catch (_e) {
        setError(e.message || "ping failed");
      }
    }
    lastPostedAtRef.current = Date.now();
    lastPostedPosRef.current = { lat: body.lat, lng: body.lng };
  }, [assetId]);

  const onPosition = useCallback((pos) => {
    setLast({
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
      speed: pos.coords.speed,
      heading: pos.coords.heading,
      ts: Date.now(),
    });
    setError(null);

    const now = Date.now();
    const elapsed = now - lastPostedAtRef.current;
    const movedFar = lastPostedPosRef.current && metersBetween(
      lastPostedPosRef.current,
      { lat: pos.coords.latitude, lng: pos.coords.longitude }
    ) > 100;
    if (lastPostedAtRef.current === 0 || elapsed >= intervalMs || movedFar) {
      post(pos);
    }
  }, [intervalMs, post]);

  const onError = useCallback((err) => {
    setError(err.message || `GeoError(${err.code})`);
    if (err.code === 1) setPermission("denied");
  }, []);

  const start = useCallback(async () => {
    if (!supported) { setError("Geolocation not supported on this device"); return false; }
    if (active) return true;
    setError(null);
    lastPostedAtRef.current = 0;
    lastPostedPosRef.current = null;
    try {
      const id = navigator.geolocation.watchPosition(onPosition, onError, {
        enableHighAccuracy: true,
        maximumAge: 5000,
        timeout: 20000,
      });
      watchIdRef.current = id;
      setActive(true);
      setPermission("granted");
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    }
  }, [active, onError, onPosition, supported]);

  const stop = useCallback(() => {
    if (watchIdRef.current != null) {
      try { navigator.geolocation.clearWatch(watchIdRef.current); } catch (_e) { /* watcher already cleared */ }
      watchIdRef.current = null;
    }
    setActive(false);
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current != null) {
        try { navigator.geolocation.clearWatch(watchIdRef.current); } catch (_e) { /* watcher already cleared */ }
      }
    };
  }, []);

  // Try to detect existing permission (best-effort; not all browsers expose it)
  useEffect(() => {
    if (!supported || !navigator.permissions) return;
    navigator.permissions.query({ name: "geolocation" }).then((p) => {
      setPermission(p.state);
      p.onchange = () => setPermission(p.state);
    }).catch(() => {});
  }, [supported]);

  return {
    supported,
    active,
    permission,
    last,
    count,
    error,
    queued: queuedRef.current,
    start,
    stop,
  };
}
